/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from "react";

interface ModeratorReport {
  id: string;
  reportType: string;
  status: string;
  offensiveType: string;
  description: string;
  reporterName: string;
  reporterEmail: string;
  createdAt: number | null;
}

interface ViolationResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Loads the report(s) against this monster (with reporter identity) to show above the actions.
  loadReports: () => Promise<ModeratorReport[]>;
  // Dismiss a single report (it was not a valid violation). Resolves true on success; the modal stays
  // open and reloads its list, like the owner resolving their own violations one at a time.
  onDismissReport: (reportId: string) => Promise<boolean>;
  // Permanently delete the reported monster. Resolves true on success; the parent closes the modal.
  onDeleteMonster: () => Promise<boolean>;
}

// Shown to an approver viewing a reported ('Violation') monster (opened via "View reports"). The monster
// is already Private (forced there by the report), so there's no "confirm" action: the moderator either
// dismisses each report that isn't a valid violation, or deletes the monster outright.
const ViolationResponseModal: React.FC<ViolationResponseModalProps> = ({ isOpen, onClose, loadReports, onDismissReport, onDeleteMonster }) => {
  const [busy, setBusy] = useState<boolean>(false);
  const [dismissingId, setDismissingId] = useState<string>('');
  const [confirmingDelete, setConfirmingDelete] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [reports, setReports] = useState<ModeratorReport[]>([]);

  const reload = () => {
    setLoading(true);
    return loadReports().then((r) => {
      setReports(r);
      setLoading(false);
    });
  };

  useEffect(() => {
    setBusy(false);
    setDismissingId('');
    setConfirmingDelete(false);
    setError('');
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    loadReports().then((r) => {
      if (!cancelled) {
        setReports(r);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const partLabel = (offensiveType: string) =>
    offensiveType === 'both' ? 'image and text' : offensiveType === 'image' ? 'image' : offensiveType === 'text' ? 'text' : '';
  const reportLabel = (r: ModeratorReport) =>
    r.reportType === 'error' ? 'Data issue'
    : r.reportType === 'copyright' ? 'Copyright violation'
    : 'Violation';
  const isResolved = (r: ModeratorReport) => r.status === 'resolved';

  const handleDismiss = async (id: string) => {
    setDismissingId(id);
    setError('');
    const ok = await onDismissReport(id);
    if (ok) {
      await reload();
    } else {
      setError('The report could not be dismissed. Please try again.');
    }
    setDismissingId('');
  };

  const handleDelete = async () => {
    setBusy(true);
    setError('');
    const ok = await onDeleteMonster();
    setBusy(false);
    if (!ok) {
      setError('The monster could not be deleted. Please try again.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ width: '560px', height: 'auto', minHeight: '280px' }} onClick={(e) => e.stopPropagation()}>
        <span className="modal-title" style={{ display: 'block', textAlign: 'center' }}>Reports Against This Monster</span>
        <div>
          {loading && <p>Loading reports…</p>}
          {!loading && reports.length === 0 && <p>No reports found.</p>}
          {!loading && reports.length > 0 && (
            <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
              {reports.map((r) => (
                <li key={r.id} style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                  <div style={{ flexShrink: 0, width: '90px', paddingTop: '2px' }}>
                    {isResolved(r) ? (
                      <span title="Resolved"><input type="checkbox" checked readOnly /> Dismissed</span>
                    ) : (
                      <button type="button" disabled={dismissingId === r.id || busy}
                              onClick={() => handleDismiss(r.id)}>
                        {dismissingId === r.id ? '…' : 'Dismiss'}
                      </button>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <b style={{ color: r.reportType === 'error' ? '#1769aa' : 'darkred' }}>{reportLabel(r)}</b>
                    {r.reportType !== 'error' && partLabel(r.offensiveType) ? ` — ${partLabel(r.offensiveType)}` : ''}
                    {r.status === 'confirmed' ? ' (confirmed)' : ''}
                    <div>{r.description}</div>
                    <span style={{ fontSize: 'small', color: '#555' }}>
                      &mdash; {r.reporterName} &lt;{r.reporterEmail}&gt;
                      {r.createdAt ? ` on ${new Date(r.createdAt).toLocaleString()}` : ''}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p style={{ fontSize: 'small' }}>
            This monster was set to <b>Private</b> when it was reported. <b>Dismiss</b> each report that
            isn't a valid violation (it's kept for the record and removed from the moderation queue); once
            none remain the monster is no longer flagged. Or <b>Delete Monster</b> to permanently remove it.
          </p>
          {error && <p style={{ color: 'darkred', fontWeight: 'bold' }}>{error}</p>}
        </div>
        <div className="modal-actions" style={{ textAlign: 'center' }}>
          {!confirmingDelete ? (
            <button type="button" disabled={busy || !!dismissingId} onClick={() => setConfirmingDelete(true)}>Delete Monster…</button>
          ) : (
            <>
              <button type="button" disabled={busy} onClick={handleDelete}
                      style={{ color: 'white', backgroundColor: 'darkred' }}>
                Confirm Delete
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmingDelete(false)}>Cancel</button>
            </>
          )}
          <button type="button" disabled={busy} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ViolationResponseModal;
